import { useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  EmptyState,
  Field,
  Select,
  Skeleton,
  TabPanels,
  useToast,
} from '@api-hub/ui';
import { PageHeader } from '../components/Layout';
import { api, describeError } from '../lib/api';
import { useResource } from '../lib/hooks';
import { prettyJson } from '../lib/format';

interface Preview {
  serverInfo: { name: string; version: string; title: string };
  instructions: string;
  instructionsPrefixLength: number;
  capabilities: Record<string, unknown>;
  tools: Array<{
    name: string;
    title: string | null;
    description: string;
    inputSchema: unknown;
    outputSchema: unknown;
    annotations: Record<string, boolean | string> | null;
    meta: Record<string, unknown> | null;
  }>;
  resources: Array<{ uri: string; name: string; title?: string; description: string }>;
  resourceTemplates: Array<{ uriTemplate: string; name: string; description: string }>;
  prompts: Array<{
    name: string;
    title: string | null;
    description: string;
    arguments: Array<{ name: string; required?: boolean }>;
  }>;
  notice: string;
}

export function McpPreviewPage() {
  const toolsets = useResource<{ items: Array<{ id: string; key: string; name: string }> }>(
    '/api/admin/mcp-toolsets',
  );
  const apps = useResource<{ items: Array<{ id: string; key: string }> }>('/api/admin/client-apps');
  const tokens = useResource<{ items: Array<{ id: string; name: string; user: string }> }>(
    '/api/admin/developer-tokens',
  );
  const toast = useToast();

  const [subjectType, setSubjectType] = useState<'toolset' | 'client_app' | 'developer_token'>(
    'toolset',
  );
  const [subjectId, setSubjectId] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const subject =
        subjectType === 'toolset'
          ? { type: 'toolset', toolsetId: subjectId }
          : subjectType === 'client_app'
            ? { type: 'client_app', clientAppId: subjectId }
            : { type: 'developer_token', developerTokenId: subjectId };
      setPreview(await api.post<Preview>('/api/admin/mcp/preview', { subject }));
    } catch (error) {
      const described = describeError(error);
      toast.error(described.title, described.body);
    } finally {
      setBusy(false);
    }
  };

  const options =
    subjectType === 'toolset'
      ? (toolsets.data?.items ?? []).map((item) => ({
          id: item.id,
          label: `${item.key} — ${item.name}`,
        }))
      : subjectType === 'client_app'
        ? (apps.data?.items ?? []).map((item) => ({ id: item.id, label: item.key }))
        : (tokens.data?.items ?? []).map((item) => ({
            id: item.id,
            label: `${item.name} (${item.user})`,
          }));

  return (
    <>
      <PageHeader
        title="Podgląd MCP"
        description="Dokładnie ta powierzchnia, którą zobaczy Codex lub Claude Code dla wybranej polityki. Podgląd niczego nie wywołuje."
      />

      <Card title="Wybierz podmiot">
        <div className="ah-grid cols-2">
          <Field label="Rodzaj">
            {(id) => (
              <Select
                id={id}
                value={subjectType}
                onChange={(event) => {
                  setSubjectType(event.target.value as typeof subjectType);
                  setSubjectId('');
                  setPreview(null);
                }}
              >
                <option value="toolset">Zestaw narzędzi MCP</option>
                <option value="client_app">Aplikacja kliencka</option>
                <option value="developer_token">Token deweloperski</option>
              </Select>
            )}
          </Field>
          <Field label="Podmiot">
            {(id) => (
              <Select
                id={id}
                value={subjectId}
                onChange={(event) => setSubjectId(event.target.value)}
              >
                <option value="">— wybierz —</option>
                {options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        <Button variant="primary" disabled={!subjectId || busy} onClick={run}>
          {busy ? 'Generuję…' : 'Pokaż powierzchnię MCP'}
        </Button>
      </Card>

      {busy && <Skeleton rows={6} />}

      {preview && (
        <>
          <Callout>{preview.notice}</Callout>
          <TabPanels
            tabs={[
              {
                value: 'tools',
                label: `Narzędzia (${preview.tools.length})`,
                content: (
                  <Card tight>
                    {preview.tools.length === 0 ? (
                      <EmptyState title="Brak narzędzi" />
                    ) : (
                      <div className="ah-card-body">
                        {preview.tools.map((tool) => (
                          <div
                            key={tool.name}
                            style={{
                              marginBottom: 20,
                              paddingBottom: 16,
                              borderBottom: '1px solid var(--ah-border)',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                gap: 8,
                                alignItems: 'center',
                                flexWrap: 'wrap',
                              }}
                            >
                              <strong className="ah-mono">{tool.name}</strong>
                              {tool.annotations?.readOnlyHint === true && (
                                <Badge tone="ok">readOnly</Badge>
                              )}
                              {tool.annotations?.destructiveHint === true && (
                                <Badge tone="danger">destructive</Badge>
                              )}
                              {tool.annotations?.idempotentHint === true && (
                                <Badge tone="info">idempotent</Badge>
                              )}
                              {tool.annotations?.openWorldHint === true && (
                                <Badge tone="neutral">openWorld</Badge>
                              )}
                            </div>
                            <p
                              style={{
                                margin: '6px 0',
                                color: 'var(--ah-text-muted)',
                                fontSize: 13,
                              }}
                            >
                              {tool.description}
                            </p>
                            {tool.meta ? (
                              <div
                                className="ah-mono"
                                style={{ fontSize: 11, color: 'var(--ah-text-subtle)' }}
                              >
                                {String(tool.meta.operationKey ?? '')}@
                                {String(tool.meta.operationVersion ?? '')}
                              </div>
                            ) : null}
                            <details style={{ marginTop: 8 }}>
                              <summary style={{ cursor: 'pointer', fontSize: 12 }}>
                                Schemat wejściowy
                              </summary>
                              <pre className="ah-code">{prettyJson(tool.inputSchema)}</pre>
                            </details>
                            {tool.outputSchema ? (
                              <details>
                                <summary style={{ cursor: 'pointer', fontSize: 12 }}>
                                  Schemat wyjściowy
                                </summary>
                                <pre className="ah-code">{prettyJson(tool.outputSchema)}</pre>
                              </details>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                ),
              },
              {
                value: 'instructions',
                label: 'Instrukcje serwera',
                content: (
                  <Card
                    title="Instrukcje"
                    description={`Pierwsze ${preview.instructionsPrefixLength} znaków jest samowystarczalne — część klientów czyta wyłącznie ten fragment.`}
                  >
                    <pre className="ah-code">{preview.instructions}</pre>
                  </Card>
                ),
              },
              {
                value: 'resources',
                label: `Zasoby (${preview.resources.length})`,
                content: (
                  <Card tight>
                    <div className="ah-card-body">
                      <h3 style={{ fontSize: 13 }}>Zasoby</h3>
                      <ul className="ah-mono" style={{ fontSize: 12 }}>
                        {preview.resources.map((resource) => (
                          <li key={resource.uri}>
                            {resource.uri}{' '}
                            <span style={{ color: 'var(--ah-text-muted)' }}>
                              — {resource.description}
                            </span>
                          </li>
                        ))}
                      </ul>
                      <h3 style={{ fontSize: 13, marginTop: 16 }}>Szablony zasobów</h3>
                      <ul className="ah-mono" style={{ fontSize: 12 }}>
                        {preview.resourceTemplates.map((template) => (
                          <li key={template.uriTemplate}>
                            {template.uriTemplate}{' '}
                            <span style={{ color: 'var(--ah-text-muted)' }}>
                              — {template.description}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Card>
                ),
              },
              {
                value: 'prompts',
                label: `Prompty (${preview.prompts.length})`,
                content: (
                  <Card tight>
                    <div className="ah-card-body">
                      {preview.prompts.map((prompt) => (
                        <div key={prompt.name} style={{ marginBottom: 12 }}>
                          <strong className="ah-mono">{prompt.name}</strong>
                          <p
                            style={{ margin: '2px 0', fontSize: 13, color: 'var(--ah-text-muted)' }}
                          >
                            {prompt.description}
                          </p>
                          <div style={{ fontSize: 12 }}>
                            Argumenty:{' '}
                            {prompt.arguments.map((argument) => (
                              <Badge
                                key={argument.name}
                                tone={argument.required ? 'info' : 'neutral'}
                              >
                                {argument.name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                ),
              },
              {
                value: 'raw',
                label: 'Surowy JSON',
                content: (
                  <Card>
                    <pre className="ah-code">{prettyJson(preview)}</pre>
                  </Card>
                ),
              },
            ]}
          />
        </>
      )}
    </>
  );
}
